import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { HOST_PATHS } from '../mentoringContent';
import { MENTORING_LOCALES, getMentoringPath } from '../mentoringRoutes';
import { SmartLink } from './MentoringPrimitives';

const LOGO_SRC = '/favicon.svg';

/**
 * Sticky section header. It deliberately replaces — rather than sits under —
 * the host site header, so the microsite reads as its own product surface.
 */
export const MentoringHeader = ({ locale, nav, openCtaTarget }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const switchLocale = (next) => {
    if (!MENTORING_LOCALES.includes(next)) {
      return;
    }

    setOpen(false);
    // Same page, other language. Sub-paths share their parent's prefix, so the
    // longest match wins — otherwise /fr/mentorat/projets would resolve back
    // to the index page.
    const activePageKey = nav.links
      .map((link) => ({
        pageKey: link.pageKey,
        path: getMentoringPath(locale, link.pageKey) ?? '',
      }))
      .filter(({ path }) => path && location.pathname.startsWith(path))
      .sort((a, b) => b.path.length - a.path.length)[0]?.pageKey ?? 'index';
    const nextPath = getMentoringPath(next, activePageKey);

    if (nextPath) {
      navigate(nextPath);
    }
  };

  const links = (className) => nav.links.map((link) => {
    const href = getMentoringPath(locale, link.pageKey);
    const isCurrent = href === location.pathname;

    return (
      <Link
        key={link.pageKey}
        to={href}
        className={className}
        aria-current={isCurrent ? 'page' : undefined}
        onClick={() => setOpen(false)}
      >
        {link.label}
      </Link>
    );
  });

  return (
    <header className={`mt-topbar${open ? ' mt-topbar--open' : ''}`}>
      <div className="mt-shell">
        <div className="mt-topbar__inner">
          <Link to={getMentoringPath(locale, 'index')} className="mt-topbar__brand">
            <img src={LOGO_SRC} alt="" width="56" height="36" />
            <span className="mt-topbar__name">
              <strong>{nav.brandName}</strong>
              <span>{nav.brand}</span>
            </span>
          </Link>

          <div className="mt-topbar__nav-desktop">
            <nav className="mt-nav" aria-label={nav.brand}>
              {links('mt-nav__link')}
            </nav>
          </div>

          <div className="mt-topbar__aside">
            <LanguageSwitch locale={locale} onSwitch={switchLocale} nav={nav} />
            {openCtaTarget && (
              <SmartLink
                href={getMentoringPath(locale, 'index')}
                className="mt-btn mt-btn--primary mt-topbar__cta"
              >
                {nav.openCta}
              </SmartLink>
            )}
            <button
              type="button"
              className="mt-burger"
              aria-expanded={open}
              aria-controls="mt-drawer"
              onClick={() => setOpen((value) => !value)}
            >
              {open ? nav.close : nav.menu}
            </button>
          </div>
        </div>

        {/* The container always exists so the button's aria-controls resolves;
            the links inside render only while the drawer is open, which keeps
            a single copy of each nav link in the accessibility tree. */}
        <div className="mt-drawer" id="mt-drawer">
          {open && (
            <>
              <nav className="mt-nav" aria-label={nav.brand}>
                {links('mt-nav__link')}
              </nav>
              <div className="mt-drawer__foot">
                <Link to="/" className="mt-nav__link" onClick={() => setOpen(false)}>
                  {nav.home}
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

const LanguageSwitch = ({ locale, onSwitch, nav }) => (
  <div className="mt-lang" role="group" aria-label={nav.languageLabel}>
    <button type="button" onClick={() => onSwitch('fr')} aria-pressed={locale === 'fr'}>
      FR
    </button>
    <span aria-hidden="true">/</span>
    <button type="button" onClick={() => onSwitch('en')} aria-pressed={locale === 'en'}>
      EN
    </button>
  </div>
);

export const MentoringFooter = ({ footer, nav, locale }) => (
  <footer className="mt-foot">
    <div className="mt-shell">
      <div className="mt-foot__grid">
        <div>
          <p className="mt-foot__title">{nav.brandName}</p>
          <p className="mt-foot__blurb">{footer.description}</p>
        </div>

        <div>
          <p className="mt-foot__title">{footer.siteTitle}</p>
          <ul className="mt-foot__list">
            {nav.links.map((link) => (
              <li key={link.pageKey}>
                <Link to={getMentoringPath(locale, link.pageKey)}>{link.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="mt-foot__title">{footer.reachTitle}</p>
          <ul className="mt-foot__list">
            <li><a href="mailto:contact@csl-brands.com">contact@csl-brands.com</a></li>
            <li><a href="tel:+237653459638">+237 653 45 96 38</a></li>
            <li><span>Douala, Littoral, Cameroun</span></li>
          </ul>
        </div>

        <div>
          <p className="mt-foot__title">Elsewhere</p>
          <ul className="mt-foot__list">
            {footer.social.map((item) => (
              <li key={item.label}>
                <a href={item.href} target="_blank" rel="noopener noreferrer">
                  {item.label} ↗
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-foot__bottom">
        <span>{footer.bottomLeft}</span>
        <span>
          {footer.legal.map((item, index) => (
            <React.Fragment key={item.label}>
              {index > 0 && ' · '}
              <SmartLink href={HOST_PATHS[item.target]}>{item.label}</SmartLink>
            </React.Fragment>
          ))}
        </span>
      </div>
    </div>
  </footer>
);
